import React, { useState, useMemo, useEffect } from 'react';
import styles from '../../../styles/ReportsDashboard.module.css';
import { useReports } from '../../../hooks/useReports';
import DocumentReports from './DocumentReports';
import ListingReports from './AgentListingReports';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import { useListingContext } from '../../../contexts/ListingContext';
import type { ListingSummary } from '../../../types/ListingTypes';

interface AgentReportsProps {
    userDetails: any;
}
type ReportTab = 'compliance' | 'listings';

const AgentReports: React.FC<AgentReportsProps> = ({ userDetails }) => {
    const [activeTab, setActiveTab] = useState<ReportTab>('compliance');
    const generatedAt = useMemo(() => new Date().toLocaleString('en-ZA'), []);

    const agencyName = userDetails?.agency?.name ?? 'Agency';
    const userName = userDetails
        ? `${userDetails.fullNames ?? ''} ${userDetails.lastName ?? ''}`.trim()
        : '';

    const { overallStats, categoriesWithStats } = useDocumentContext();
    const [listings, setListings] = useState<ListingSummary[]>([]);
    const [listingsLoading, setListingsLoading] = useState(true);
    const { fetchUserListings } = useListingContext();

    const agentRows = [{
        id: userDetails?.id,
        name: userDetails?.displayName,
        email: userDetails?.email,
        completionRate: overallStats.completionRate,
    }]
    const { downloadPDF, isGenerating } = useReports({
        listings,
        agentRows,
        overallStats,
        categoriesWithStats
    });


    useEffect(() => {
        setListingsLoading(true);
        let fetchPromise = fetchUserListings(userDetails.id);

        if (!fetchPromise) {
            setListingsLoading(false);
            return;
        }

        fetchPromise
            .then(setListings)
            .catch(() => { })
            .finally(() => setListingsLoading(false));
    }, [
        userDetails?.id,
        userDetails?.agency?.id,
        fetchUserListings
    ]);

    return (
        <div className={styles.reports}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Agent Compliance &amp; Listings Report</h1>
                <p className={styles.pageSubtitle}>
                    {agencyName} &nbsp;·&nbsp; Generated {generatedAt}
                </p>
                {userName && (
                    <p className={styles.pageSubtitle}>
                        Prepared for: {userName} ({userDetails?.role})
                    </p>
                )}
            </div>
            {/* ── Tab bar + action buttons ── */}
            <div className={styles.toolbar}>
                <div className={styles.tabs}>
                    <button
                        className={`${styles.tab} ${activeTab === 'compliance' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('compliance')}
                    >
                        📄 Document Compliance
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'listings' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('listings')}
                    >
                        🏠 Listings
                    </button>
                </div>
                <div className={styles.actionButtons}>
                    <button
                        className={styles.printBtn}
                        onClick={() => downloadPDF({ userDetails })}
                        disabled={isGenerating}
                    >
                        {isGenerating ? '⏳ Generating…' : '⬇ Export PDF'}
                    </button>
                </div>
            </div>
            <div className={styles.reportBody}>
                {/* Report header */}
                {/* ── Printable report body ── */}
                {/* ── Document Compliance ── */}
                {activeTab === 'compliance' && (
                    <DocumentReports overallStats={overallStats} categoriesWithStats={categoriesWithStats} managerView={false} />
                )}

                {/* ── Listings ── */}
                {activeTab === 'listings' && (
                    <ListingReports userDetails={userDetails} listings={listings} listingsLoading={listingsLoading} />
                )}

            </div>
        </div>

    )
}

export default AgentReports;